import { ValueLabelItem } from "@custom-types/types";
import { debounce } from "es-toolkit";
import { useEffect, useMemo, useState } from "react";

/** Time frame to show analytics in months */
export type TimeFrameItem = ValueLabelItem<number>;

/** Time frames to show analytics with value in months and display label */
export const TIME_FRAMES: TimeFrameItem[] = [
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 2 years" },
  { value: 36, label: "Last 3 years" },
];

/** Available time frames to show analytics in months */
export const AVAILABLE_TIME_FRAMES = TIME_FRAMES.map(
  (timeFrame) => timeFrame.value
);

/**
 * Debounces a value with the specified delay.
 *
 * @param value The value to be debounced.
 * @param delay The delay in milliseconds.
 * @param debouncingStart Optional callback that will be triggered immediately before the debouncing starts.
 * @param debouncingEnd Optional callback that will be triggered immediately after debouncing ends.
 * @returns The debounced value.
 */
export function useDebounce(
  value: string,
  delay: number,
  debouncingStart?: () => void,
  debouncingEnd?: () => void
): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  // Recreate the debounced function only when delay/end callback changes.
  const debouncedUpdate = useMemo(
    () =>
      debounce((newValue: string) => {
        setDebouncedValue(newValue);
        debouncingEnd?.();
      }, delay),
    [delay, debouncingEnd]
  );

  useEffect(() => {
    // Notify that a new debounce cycle started and schedule the update.
    debouncingStart?.();
    debouncedUpdate(value);

    // Cancel any pending update when value/debounce function changes or on unmount.
    return () => {
      debouncedUpdate.cancel();
    };
  }, [value, debouncedUpdate, debouncingStart]);

  return debouncedValue;
}

/**
 * Returns a delay as a Promise that can be awaited. This helps to wait for a certain amount of time in the code
 *
 * @param ms The delay in milliseconds
 * @returns A Promise that resolves after the specified delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
